import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import {
  bulkImportControlsDraft,
  fetchSoxControl,
  type ControlBulkImportBody,
  type BulkImportControlsResult,
} from '../../request/controls/controls.api.ts';
import { fetchControlsListPage, type ControlsListPageResult } from '../../request/controls/controlsList.ts';
import { fetchControlsDashboardMetrics } from '../../request/controls/controlsDashboard.ts';
import { patchControlUpdateRiskMappings } from '../../request/controls/controlRiskMappings.ts';
import { formValuesToApiPayload } from '../../features/controls/functional.ts';
import type { ControlFormValues } from '../../features/controls/schema.ts';
import { controlsQueryKeys, type ControlsListParams } from '../../queries/controls/keys.ts';

export type CreateControlInput = {
  formValues: ControlFormValues;
  riskMappings?: { riskId: string; processIds: string[] }[];
  itgcRiskIds?: string[];
  auditYearId?: string;
};

/** Detail query key — includes audit year so cache invalidates on year switch. */
export const soxControlQueryKey = (controlId: string, auditYearId?: string | null) =>
  ['sox-control', controlId, auditYearId ?? null] as const;

export function useControlsList(
  params: ControlsListParams
): UseQueryResult<ControlsListPageResult, Error> {
  return useQuery({
    queryKey: controlsQueryKeys.list(params),
    queryFn: () => fetchControlsListPage(params),
    placeholderData: keepPreviousData,
  });
}

export type ControlsDashboardMetricsView = Awaited<ReturnType<typeof fetchControlsDashboardMetrics>>;

export function useControlsDashboardMetrics(): UseQueryResult<ControlsDashboardMetricsView, Error> {
  return useQuery({
    queryKey: controlsQueryKeys.dashboard(),
    queryFn: fetchControlsDashboardMetrics,
  });
}

export const useSoxControl = (
  controlId: string | undefined
): UseQueryResult<Record<string, unknown>, Error> => {
  const { selectedAuditYearId } = useAuditYear();
  return useQuery({
    queryKey: soxControlQueryKey(controlId ?? '', selectedAuditYearId),
    queryFn: () => fetchSoxControl(controlId!, selectedAuditYearId),
    enabled: Boolean(controlId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useCreateControl = (): UseMutationResult<unknown, Error, CreateControlInput> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CreateControlInput>({
    mutationFn: async ({ formValues, riskMappings, itgcRiskIds, auditYearId }) => {
      const response = await axiosInstance.post('/api/sox-controls', {
        ...formValuesToApiPayload(formValues, { riskMappings, itgcRiskIds }),
        status: 'draft',
        ...(auditYearId ? { audit_year_id: auditYearId } : {}),
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: controlsQueryKeys.all });
      const record = data as { control_id_was_deduped?: boolean; control_id?: string; requested_control_id?: string };
      if (record?.control_id_was_deduped) {
        toast.success(
          `Saved as ${record.control_id} — "${record.requested_control_id}" was already in use.`
        );
      } else {
        toast.success('Control created as draft');
      }
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to create control')));
    },
  });
};

export const useUpdateControl = (): UseMutationResult<
  unknown,
  Error,
  { controlUuid: string; payload: Record<string, unknown> }
> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { controlUuid: string; payload: Record<string, unknown> }>({
    mutationFn: async ({ controlUuid, payload }) => {
      const response = await axiosInstance.put(`/api/sox-controls/${controlUuid}`, {
        uuid: controlUuid,
        ...payload,
      });
      return response.data;
    },
    onSuccess: (_data, { controlUuid }) => {
      queryClient.invalidateQueries({ queryKey: controlsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: soxControlQueryKey(controlUuid) });
      toast.success('Control updated successfully');
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to update control')));
    },
  });
};

export const usePatchControlUpdateRiskMappings = (): UseMutationResult<
  Record<string, unknown>,
  Error,
  { controlUuid: string; payload: Record<string, unknown> }
> => {
  const queryClient = useQueryClient();

  return useMutation<Record<string, unknown>, Error, { controlUuid: string; payload: Record<string, unknown> }>({
    mutationFn: async ({ controlUuid, payload }) => patchControlUpdateRiskMappings(controlUuid, payload),
    onSuccess: (_data, { controlUuid }) => {
      queryClient.invalidateQueries({ queryKey: controlsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: soxControlQueryKey(controlUuid) });
      queryClient.invalidateQueries({ queryKey: ['controls', 'risk-with-process-mappings-options'] });
      queryClient.invalidateQueries({ queryKey: ['controls', 'process-with-risk-mapping-options'] });
      toast.success('Control updated successfully');
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to update control')));
    },
  });
};

export const useBulkImportControls = (): UseMutationResult<
  BulkImportControlsResult,
  Error,
  ControlBulkImportBody
> => {
  const queryClient = useQueryClient();

  return useMutation<BulkImportControlsResult, Error, ControlBulkImportBody>({
    mutationFn: bulkImportControlsDraft,
    onSuccess: async (data) => {
      toast.success(data?.message || 'Controls imported as drafts.');
      await queryClient.invalidateQueries({ queryKey: controlsQueryKeys.all });
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to import controls.')));
    },
  });
};
